import { Box, Flex, FormControl } from "@chakra-ui/react";
import { useEffect } from "react";
import { useSlide } from "../public/slide";
import { SlidePropsDate, DateValue } from "../public/types";
import { DateState, useQuizActions, useQuizSnapshot } from "./state";
import { MyFormLabel, MySelect } from "./ui";
import { useTranslation } from "gatsby-plugin-react-i18next";

const DEFAULT_DATE: DateValue = { year: 1990, month: 1, day: 1 };

function getMonthNames(t: any) {
  return [
    t("quizLib.datepicker.months.january", { defaultValue: "January" }),
    t("quizLib.datepicker.months.february", { defaultValue: "February" }),
    t("quizLib.datepicker.months.march", { defaultValue: "March" }),
    t("quizLib.datepicker.months.april", { defaultValue: "April" }),
    t("quizLib.datepicker.months.may", { defaultValue: "May" }),
    t("quizLib.datepicker.months.june", { defaultValue: "June" }),
    t("quizLib.datepicker.months.july", { defaultValue: "July" }),
    t("quizLib.datepicker.months.august", { defaultValue: "August" }),
    t("quizLib.datepicker.months.september", { defaultValue: "September" }),
    t("quizLib.datepicker.months.october", { defaultValue: "October" }),
    t("quizLib.datepicker.months.november", { defaultValue: "November" }),
    t("quizLib.datepicker.months.december", { defaultValue: "December" }),
  ];
}

/** Month's are 0 indexed */
function getNumberOfDaysInMonth(year: number, month: number) {
  // 0 is the last day of the previous month, so that's why we add +1 to a month here
  return new Date(year, month + 1, 0).getDate();
}

const years = Array(100)
  .fill(0)
  .map((_, i) => new Date().getFullYear() - i);

export type DatePickerProps = {} & SlidePropsDate;

export function DatePicker(_: DatePickerProps) {
  const slide = useSlide();
  const snap = useQuizSnapshot();
  const actions = useQuizActions();
  const { t } = useTranslation();
  const months = getMonthNames(t);

  useEffect(() => {
    actions.setDateValue(slide.id, {
      ...DEFAULT_DATE,
    });
  }, []);

  const state = snap.slideStateByID[slide.id] as DateState;

  return (
    <Flex width={"full"} gap={2}>
      <Box flex={4} width={"full"}>
        <FormControl>
          <MyFormLabel>{t("quizLib.datepicker.year", { defaultValue: "Year" })}</MyFormLabel>
          <MySelect
            onChange={(e) => {
              const v = parseInt(e.target.value);
              actions.setDateValue(slide.id, {
                ...DEFAULT_DATE,
                ...state.value,
                year: v,
              });
            }}
            value={state.value?.year ?? DEFAULT_DATE.year}
          >
            {years.map((year) => (
              <option value={year} key={year}>
                {year}
              </option>
            ))}
          </MySelect>
        </FormControl>
      </Box>
      <Box flex={5} width={"full"}>
        <FormControl>
          <MyFormLabel>{t("quizLib.datepicker.month", { defaultValue: "Month" })}</MyFormLabel>
          <MySelect
            onChange={(e) => {
              const v = parseInt(e.target.value);
              actions.setDateValue(slide.id, {
                ...DEFAULT_DATE,
                ...state.value,
                month: v,
              });
            }}
            value={state.value?.month ?? DEFAULT_DATE.month}
          >
            {months.map((month, idx) => (
              <option value={idx + 1} key={month}>
                {month}
              </option>
            ))}
          </MySelect>
        </FormControl>
      </Box>
      <Box flex={3} width={"full"}>
        <FormControl>
          <MyFormLabel>{t("quizLib.datepicker.day", { defaultValue: "Day" })}</MyFormLabel>
          <MySelect
            onChange={(e) => {
              const v = parseInt(e.target.value);
              actions.setDateValue(slide.id, {
                ...DEFAULT_DATE,
                ...state.value,
                day: v,
              });
            }}
            value={state.value?.day ?? DEFAULT_DATE.day}
          >
            {Array(
              getNumberOfDaysInMonth(
                state.value?.year ?? 1900,
                state.value?.month ? state.value.month - 1 : 0
              )
            )
              .fill("")
              .map((_, idx) => {
                let day = idx + 1;
                return (
                  <option value={day} key={day}>
                    {day}
                  </option>
                );
              })}
          </MySelect>
        </FormControl>
      </Box>
    </Flex>
  );
}

export default DatePicker;
